import { computed, getCurrentScope, onScopeDispose, reactive, ref } from "vue";
import { getApiErrorCode, resolveApiErrorKey } from "@/infrastructure/services/ApiErrorService";
import { logger } from "@/infrastructure/services/LoggerService";
import { createEmptyWorkspaceSettings, } from "../domain/entities/workspaceSettings";
import { getWorkspaceSettings, updateWorkspaceSettings, } from "../infrastructure/adapters/workspaceSettingsService";
import { hasErrors, validateOrganizationQualification, validateReceiptObject, validateSignatoryFunction, validateSignatoryName, validateSignatureFile, validateSignaturePresent, } from "./useWorkspaceSettingsValidation";
const EDITABLE_FIELDS = [
    "signatoryFunction",
    "signatoryName",
    "receiptObject",
    "organizationQualification",
];
const ERR = "workspace.settings.errors";
/**
 * OPUS refusals on a field this form edits (`UpdateClientLegalInfoCommandValidator` codes, forwarded
 * by the BFF): shown under that field, exactly like the same refusal raised client-side.
 */
const FIELD_ERROR_CODES = {
    SIGNATORY_NAME_TOO_LONG: { field: "signatoryName", key: `${ERR}.signatoryNameTooLong` },
    RECEIPT_OBJECT_TOO_LONG: { field: "receiptObject", key: `${ERR}.receiptObjectTooLong` },
    ORGANIZATION_QUALIFICATION_TOO_LONG: {
        field: "organizationQualification",
        key: `${ERR}.organizationQualificationTooLong`,
    },
    SIGNATURE_INVALID_TYPE: { field: "signature", key: `${ERR}.signatureFormat` },
    SIGNATURE_TOO_LARGE: { field: "signature", key: `${ERR}.signatureSize` },
};
/**
 * OPUS refusals on the identity fields this form re-sends but does not edit (SIREN, SIRET, RNA):
 * nothing here can fix them, so they are reported on the form as a whole.
 */
const FORM_ERROR_CODES = {
    LegalIdentifierRequired: `${ERR}.identifierRequired`,
    SIREN_INVALID_FORMAT: `${ERR}.sirenFormat`,
    SIRET_INVALID_FORMAT: `${ERR}.siretFormat`,
    RNA_INVALID_FORMAT: `${ERR}.rnaFormat`,
};
// OPUS clears a column on "" only: a spaces-only value, let through when completeness is waived,
// would be stored as is.
function clearIfBlank(value) {
    return value.trim() ? value : "";
}
function revokeBlobUrl(url) {
    if (url?.startsWith("blob:"))
        URL.revokeObjectURL(url);
}
/**
 * Form over the workspace legal info (OPUS) scoped to the fields printed on the tax receipts:
 * signatory, receipt object, organisation qualification and the signature image. The other
 * fields (name, address, SIREN, SIRET, RNA) are read here and re-sent untouched, because the
 * endpoint is a full replace where an omitted value clears the column.
 */
export function useWorkspaceLegalInfoForm() {
    const settings = reactive(createEmptyWorkspaceSettings());
    // A ref, not a `let`: the dirty computed must re-run after a save that stores equal values.
    const snapshot = ref(JSON.stringify(settings));
    const isLoading = ref(false);
    const isSaving = ref(false);
    const loadError = ref(false);
    const saveErrorKey = ref(null);
    const errors = ref({});
    const pendingSignatureFile = ref(null);
    const signatureRemoved = ref(false);
    // Baseline the signature Cancel restores to: always a server URL or null, captured once per
    // edit session so a revoked blob can never become the restore target.
    const preModalSignatureUrl = ref(null);
    const isEditingSignature = computed(() => pendingSignatureFile.value !== null || signatureRemoved.value);
    const isDirty = computed(() => isEditingSignature.value || JSON.stringify(settings) !== snapshot.value);
    // A view can unmount without passing through reset() (shell re-mount, logout); the scope end
    // releases a pending blob. A server URL never starts with "blob:", so it is never touched.
    if (getCurrentScope())
        onScopeDispose(() => revokeBlobUrl(settings.signatureUrl));
    // `requireComplete: false` waives the blank checks and keeps the length and file ones: OPUS stores
    // a blank field, the requirement is a tax-receipt one that only holds while receipts are issued.
    function validate(requireComplete = true) {
        const next = {};
        const check = (value, validator) => requireComplete || value.trim() ? validator(value) : null;
        const signatoryFunction = check(settings.signatoryFunction, validateSignatoryFunction);
        if (signatoryFunction)
            next.signatoryFunction = signatoryFunction;
        const signatoryName = check(settings.signatoryName, validateSignatoryName);
        if (signatoryName)
            next.signatoryName = signatoryName;
        const receiptObject = check(settings.receiptObject, validateReceiptObject);
        if (receiptObject)
            next.receiptObject = receiptObject;
        const qualification = check(settings.organizationQualification, validateOrganizationQualification);
        if (qualification)
            next.organizationQualification = qualification;
        const signature = validateSignatureFile(pendingSignatureFile.value) ??
            (requireComplete ? validateSignaturePresent(settings.signatureUrl) : null);
        if (signature)
            next.signature = signature;
        return next;
    }
    const isValid = computed(() => !hasErrors(validate()));
    /** Valid once the blank checks are waived, see `save({ requireComplete: false })`. */
    const isValidIncomplete = computed(() => !hasErrors(validate(false)));
    function applySnapshot(next) {
        if (settings.signatureUrl !== preModalSignatureUrl.value)
            revokeBlobUrl(settings.signatureUrl);
        Object.assign(settings, next);
        snapshot.value = JSON.stringify(settings);
        pendingSignatureFile.value = null;
        signatureRemoved.value = false;
        preModalSignatureUrl.value = null;
        errors.value = {};
        saveErrorKey.value = null;
    }
    async function load() {
        isLoading.value = true;
        loadError.value = false;
        try {
            applySnapshot(await getWorkspaceSettings());
        }
        catch (err) {
            logger.error("Failed to load workspace legal info", err);
            loadError.value = true;
        }
        finally {
            isLoading.value = false;
        }
    }
    function setField(field, value) {
        settings[field] = value;
        if (errors.value[field]) {
            const next = { ...errors.value };
            delete next[field];
            errors.value = next;
        }
        saveErrorKey.value = null;
    }
    function onSignatureUpload(file) {
        const error = validateSignatureFile(file);
        if (error) {
            errors.value = { ...errors.value, signature: error };
            return;
        }
        if (!isEditingSignature.value)
            preModalSignatureUrl.value = settings.signatureUrl;
        if (settings.signatureUrl !== preModalSignatureUrl.value)
            revokeBlobUrl(settings.signatureUrl);
        pendingSignatureFile.value = file;
        signatureRemoved.value = false;
        settings.signatureUrl = URL.createObjectURL(file);
        const next = { ...errors.value };
        delete next.signature;
        errors.value = next;
        saveErrorKey.value = null;
    }
    function onSignatureRemove() {
        if (!isEditingSignature.value)
            preModalSignatureUrl.value = settings.signatureUrl;
        if (settings.signatureUrl !== preModalSignatureUrl.value)
            revokeBlobUrl(settings.signatureUrl);
        pendingSignatureFile.value = null;
        signatureRemoved.value = true;
        settings.signatureUrl = null;
        saveErrorKey.value = null;
    }
    function onSignatureCancel() {
        if (settings.signatureUrl !== preModalSignatureUrl.value)
            revokeBlobUrl(settings.signatureUrl);
        settings.signatureUrl = preModalSignatureUrl.value;
        pendingSignatureFile.value = null;
        signatureRemoved.value = false;
        preModalSignatureUrl.value = null;
    }
    function reset() {
        if (isEditingSignature.value)
            onSignatureCancel();
        const previous = JSON.parse(snapshot.value);
        for (const field of EDITABLE_FIELDS)
            settings[field] = previous[field];
        errors.value = {};
        saveErrorKey.value = null;
    }
    async function save({ requireComplete = true } = {}) {
        const nextErrors = validate(requireComplete);
        errors.value = nextErrors;
        if (hasErrors(nextErrors))
            return "invalid";
        // Identity fields come from the last load, never from this form: the endpoint replaces
        // every column, and an empty value would clear one the workspace page owns.
        const loaded = JSON.parse(snapshot.value);
        const payload = {
            address: loaded.address,
            siren: loaded.siren,
            siret: loaded.siret,
            rna: loaded.rna,
            signatoryFunction: clearIfBlank(settings.signatoryFunction),
            signatoryName: clearIfBlank(settings.signatoryName),
            receiptObject: clearIfBlank(settings.receiptObject),
            organizationQualification: clearIfBlank(settings.organizationQualification),
        };
        isSaving.value = true;
        try {
            const saved = await updateWorkspaceSettings(payload, pendingSignatureFile.value, signatureRemoved.value || undefined);
            // The PUT echo can omit the organisation name (observed null on a local tenant whose GET
            // carried it); the name is read-only here, so the loaded value stays authoritative.
            applySnapshot({ ...saved, name: saved.name || settings.name });
            return "saved";
        }
        catch (err) {
            logger.error("Failed to save workspace legal info", err);
            const fieldError = FIELD_ERROR_CODES[getApiErrorCode(err) ?? ""];
            if (fieldError) {
                errors.value = { ...errors.value, [fieldError.field]: fieldError.key };
                saveErrorKey.value = `${ERR}.formInvalid`;
                return "failed";
            }
            const key = resolveApiErrorKey(err, FORM_ERROR_CODES, "workspace.settings.saveError");
            if (key !== "workspace.settings.saveError")
                errors.value = { ...errors.value, form: key };
            saveErrorKey.value = key;
            return "failed";
        }
        finally {
            isSaving.value = false;
        }
    }
    return {
        settings,
        errors,
        isLoading,
        isSaving,
        loadError,
        isDirty,
        isValid,
        isValidIncomplete,
        saveErrorKey,
        load,
        setField,
        onSignatureUpload,
        onSignatureRemove,
        onSignatureCancel,
        reset,
        save,
    };
}
